import { useCallback, useRef, useState } from 'react';

import { useSessionResult } from '../SessionSummary/useSessionResult';
import type { ProgramApiFetch } from '../ProgramEditor/types';
import type { EditingTarget } from './LoggerScreen';
import type { LoggerMode } from './loggerMode';

/** The imperative handle the host's post-session summary card exposes for
 *  the header/post-save-row share affordances. RW-18 + the 2026-08-18 share-
 *  card web lift: `ShareCardRenderer` + the capture/share state machine
 *  (`useShareCardHandle`) are both shared (`packages/ui/src/Logger/`) —
 *  mobile and web-rnw each wire this handle to a real `share()`, only the
 *  post-capture step differs (native share sheet vs. Web Share API/
 *  download, see apps/web-rnw/src/logger/shareCardCaptureWeb.ts). */
export type PostSessionSummaryHandle = {
  share: () => Promise<void>;
  /** Whether a capture is currently in flight (disables the host's own
   *  in-card Share button while true). Required so every host's ref
   *  shape is structurally identical — not read by usePostSaveSummary
   *  itself, only by whatever the host renders inside the card. */
  isSharing: () => boolean;
};

// Post-save summary + share state, extracted from useLoggerScreen (500-line
// cap). One concept: what the Logger shows AFTER a save instead of closing,
// and the imperative handle the header's share icon reaches it through.
// Called at the exact position it occupied inline, so hook order is unchanged.
//
// `setPostSaveSummary` goes back out because useSaveWorkout writes it on a
// 'sent' save — the summary is raised BY the save, not by this hook.
export function usePostSaveSummary(
  editing: EditingTarget | null,
  mode: LoggerMode,
  apiFetch: ProgramApiFetch,
) {
  // Post-save summary state. After a 'sent' save we don't close the
  // modal immediately — we switch the body into "summary mode" so the
  // user sees the PostSessionSummaryCard with Share + Done CTAs.
  // 'queued' saves skip the summary (no server-computed SessionResult
  // available yet) and follow the old close-immediately path.
  const [postSaveSummary, setPostSaveSummary] = useState<{
    sessionId: string;
    onDone: () => void;
  } | null>(null);
  // Pull the post-session summary when editing a saved workout (mode='edit'
  // only — 'repeat' clones a template and the new session id isn't on the
  // server yet). Hook tolerates null and short-circuits when no id supplied.
  // PE-22 K0 — 'prescribe' answers null here, and the literal `=== 'log-past'`
  // is what makes that true BY CONSTRUCTION rather than by luck: a prescription
  // has no server-side session, so there is no SessionResult to fetch and no
  // summary to show. Stated because the next reader will wonder whether the
  // third mode was considered; it was, and the answer is "nothing to pull".
  const summarySessionId = postSaveSummary
    ? postSaveSummary.sessionId
    : editing !== null && mode === 'log-past'
      ? editing.sessionId
      : null;
  const sessionResult = useSessionResult({ apiFetch, sessionId: summarySessionId });
  // Imperative ref into the host's post-session summary card for
  // header-icon share + post-save-row share. Card is only mounted when
  // sessionResult is present, so callers must null-check before invoking.
  const summaryCardRef = useRef<PostSessionSummaryHandle>(null);
  const [headerSharing, setHeaderSharing] = useState(false);
  const triggerHeaderShare = useCallback(async () => {
    if (!summaryCardRef.current) return;
    setHeaderSharing(true);
    try {
      await summaryCardRef.current.share();
    } finally {
      setHeaderSharing(false);
    }
  }, []);

  return {
    postSaveSummary,
    setPostSaveSummary,
    summarySessionId,
    sessionResult,
    summaryCardRef,
    headerSharing,
    triggerHeaderShare,
  };
}
